import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';

interface CalendarDatePickerProps {
  dateFrom?: string;
  dateTo?: string;
  onChange: (dateFrom: string, dateTo: string) => void;
  onKeyPress?: (e: React.KeyboardEvent) => void;
}

type PresetType = 'today' | 'yesterday' | 'thisMonth' | 'thisYear' | 'lastWeek' | 'lastMonth' | 'custom';

interface DatePreset {
  id: PresetType;
  label: string;
  getValue: () => { dateFrom: string; dateTo: string };
}

const CalendarDatePicker: React.FC<CalendarDatePickerProps> = ({
  dateFrom,
  dateTo,
  onChange,
  onKeyPress
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState<PresetType>('yesterday');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectingRange, setSelectingRange] = useState<{start?: string; end?: string}>({});
  const [hoverDate, setHoverDate] = useState<string>('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Date utilities
  const formatDate = useCallback((date: Date): string => {
    return date.toISOString().split('T')[0];
  }, []);

  const parseDate = useCallback((dateStr: string): Date => {
    return new Date(dateStr + 'T00:00:00');
  }, []);

  const getToday = useCallback(() => {
    return formatDate(new Date());
  }, [formatDate]);

  const getYesterday = useCallback(() => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return formatDate(yesterday);
  }, [formatDate]);

  const getThisMonthRange = useCallback(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
      dateFrom: formatDate(start),
      dateTo: formatDate(end)
    };
  }, [formatDate]);

  const getThisYearRange = useCallback(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 1);
    const end = new Date(now.getFullYear(), 11, 31);
    return {
      dateFrom: formatDate(start),
      dateTo: formatDate(end)
    };
  }, [formatDate]);

  const getLastWeekRange = useCallback(() => {
    const now = new Date();
    const lastWeekEnd = new Date(now);
    lastWeekEnd.setDate(now.getDate() - 1); // Yesterday
    const lastWeekStart = new Date(lastWeekEnd);
    lastWeekStart.setDate(lastWeekEnd.getDate() - 6); // 7 days ago
    return {
      dateFrom: formatDate(lastWeekStart),
      dateTo: formatDate(lastWeekEnd)
    };
  }, [formatDate]);

  const getLastMonthRange = useCallback(() => {
    const now = new Date();
    const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
    return {
      dateFrom: formatDate(lastMonthStart),
      dateTo: formatDate(lastMonthEnd)
    };
  }, [formatDate]);

  // Preset configurations
  const presets: DatePreset[] = useMemo(() => [
    {
      id: 'today',
      label: 'Today',
      getValue: () => {
        const today = getToday();
        return { dateFrom: today, dateTo: today };
      }
    },
    {
      id: 'yesterday',
      label: 'Yesterday',
      getValue: () => {
        const yesterday = getYesterday();
        return { dateFrom: yesterday, dateTo: yesterday };
      }
    },
    {
      id: 'thisMonth',
      label: 'This month',
      getValue: getThisMonthRange
    },
    {
      id: 'thisYear',
      label: 'This year',
      getValue: getThisYearRange
    },
    {
      id: 'lastWeek',
      label: 'Last week',
      getValue: getLastWeekRange
    },
    {
      id: 'lastMonth',
      label: 'Last month',
      getValue: getLastMonthRange
    }
  ], [getToday, getYesterday, getThisMonthRange, getThisYearRange, getLastWeekRange, getLastMonthRange]);

  // Detect current preset
  const detectPreset = useCallback((from?: string, to?: string): PresetType => {
    if (!from || !to) return 'yesterday';
    
    for (const preset of presets) {
      const value = preset.getValue();
      if (value.dateFrom === from && value.dateTo === to) {
        return preset.id;
      }
    }
    return 'custom';
  }, [presets]);

  // Update preset and selection state when props change
  useEffect(() => {
    if (dateFrom && dateTo) {
      const preset = detectPreset(dateFrom, dateTo);
      setSelectedPreset(preset);
      
      // Reset selecting range state when external props change
      // This ensures we're not in a partial selection state
      if (dateFrom !== dateTo) {
        // We have a complete range, clear selection state
        setSelectingRange({});
      } else if (!selectingRange.start) {
        // Single date selected but no selection state, set it
        setSelectingRange({ start: dateFrom });
      }
    }
  }, [dateFrom, dateTo, detectPreset, selectingRange.start]);

  // Handle preset selection
  const handlePresetSelect = useCallback((preset: DatePreset) => {
    const value = preset.getValue();
    setSelectedPreset(preset.id);
    setSelectingRange({}); // Clear range selection state
    onChange(value.dateFrom, value.dateTo);
    if (preset.id !== 'custom') {
      setIsOpen(false);
    }
  }, [onChange]);

  // Generate calendar days for a month
  const generateCalendarDays = useCallback((month: Date) => {
    const year = month.getFullYear();
    const monthIndex = month.getMonth();
    const firstDay = new Date(year, monthIndex, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay()); // Start from Sunday
    
    const days = [];
    const current = new Date(startDate);
    
    for (let i = 0; i < 42; i++) { // 6 weeks
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    
    return days;
  }, []);

  // Handle date click
  const handleDateClick = useCallback((clickedDate: string) => {
    // If we're in the middle of selecting a range (have start but no end)
    if (selectingRange.start && !selectingRange.end) {
      // Complete the range selection
      const startDate = selectingRange.start;
      if (clickedDate === startDate) {
        // Clicked same date, keep it as single date
        onChange(clickedDate, clickedDate);
        setSelectingRange({});
      } else {
        // Complete range selection
        if (clickedDate < startDate) {
          onChange(clickedDate, startDate);
        } else {
          onChange(startDate, clickedDate);
        }
        setSelectingRange({});
      }
      setSelectedPreset('custom');
    } else {
      // Start new range selection
      onChange(clickedDate, clickedDate);
      setSelectingRange({ start: clickedDate });
    }
  }, [onChange, selectingRange]);

  // Check if date is in range (selected range)
  const isDateInRange = useCallback((date: string, from?: string, to?: string) => {
    if (!from || !to) return false;
    if (from === to) return date === from;
    return date >= from && date <= to;
  }, []);

  // Check if date is in hover preview range
  const isDateInHoverRange = useCallback((date: string, hover?: string) => {
    // Only show hover range when we're selecting a range (have start but no end)
    if (!hover || !selectingRange.start || selectingRange.end) return false;
    
    const start = selectingRange.start;
    const end = hover;
    
    // Don't show hover range if hovering over the start date
    if (end === start) return false;
    
    const rangeStart = start < end ? start : end;
    const rangeEnd = start < end ? end : start;
    
    return date >= rangeStart && date <= rangeEnd && date !== start;
  }, [selectingRange]);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Format display text
  const getDisplayText = useCallback(() => {
    if (!dateFrom || !dateTo) return 'Select dates';
    
    const fromDate = parseDate(dateFrom);
    const toDate = parseDate(dateTo);
    
    const formatOptions: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    };
    
    if (dateFrom === dateTo) {
      return fromDate.toLocaleDateString('en-US', formatOptions);
    } else {
      return `${fromDate.toLocaleDateString('en-US', formatOptions)} - ${toDate.toLocaleDateString('en-US', formatOptions)}`;
    }
  }, [dateFrom, dateTo, parseDate]);

  // Calendar navigation
  const navigateMonth = useCallback((direction: 'prev' | 'next') => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + (direction === 'next' ? 1 : -1));
      return newMonth;
    });
  }, []);

  // Generate months to display
  const currentMonthDays = generateCalendarDays(currentMonth);
  const nextMonth = new Date(currentMonth);
  nextMonth.setMonth(currentMonth.getMonth() + 1);
  const nextMonthDays = generateCalendarDays(nextMonth);

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const dayNames = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  return (
    <div className="calendar-date-picker" ref={dropdownRef}>
      <button
        className="date-picker-trigger"
        onClick={() => setIsOpen(!isOpen)}
        onKeyPress={onKeyPress}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
      >
        <span className="date-display-text">{getDisplayText()}</span>
        <span className="dropdown-arrow">{isOpen ? '▲' : '▼'}</span>
      </button>

      {isOpen && (
        <div className="calendar-dropdown">
          <div className="calendar-content">
            <div className="presets-sidebar">
              {presets.map(preset => (
                <button
                  key={preset.id}
                  className={`preset-option ${selectedPreset === preset.id ? 'active' : ''}`}
                  onClick={() => handlePresetSelect(preset)}
                >
                  {preset.label}
                </button>
              ))}
            </div>

            <div className="calendars-section">
              <div className="calendar-navigation">
                <button
                  className="nav-button"
                  onClick={() => navigateMonth('prev')}
                  aria-label="Previous month"
                >
                  ‹
                </button>
                <span className="month-year">
                  {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                </span>
                <span className="month-year">
                  {monthNames[nextMonth.getMonth()]} {nextMonth.getFullYear()}
                </span>
                <button
                  className="nav-button"
                  onClick={() => navigateMonth('next')}
                  aria-label="Next month"
                >
                  ›
                </button>
              </div>

              <div className="dual-calendar">
                {[currentMonthDays, nextMonthDays].map((monthDays, monthIndex) => (
                  <div key={monthIndex} className="calendar-month">
                    <div className="day-headers">
                      {dayNames.map(day => (
                        <div key={day} className="day-header">{day}</div>
                      ))}
                    </div>
                    <div className="calendar-grid">
                      {monthDays.map((day, dayIndex) => {
                        const dateStr = formatDate(day);
                        const isCurrentMonth = day.getMonth() === (monthIndex === 0 ? currentMonth : nextMonth).getMonth();
                        const isToday = dateStr === getToday();
                        const isSelected = isDateInRange(dateStr, dateFrom, dateTo);
                        const isInHoverRange = isDateInHoverRange(dateStr, hoverDate);
                        const isRangeStart = selectingRange.start === dateStr;
                        
                        return (
                          <button
                            key={dayIndex}
                            className={`calendar-day ${!isCurrentMonth ? 'other-month' : ''} ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''} ${isInHoverRange ? 'hover-range' : ''} ${isRangeStart ? 'range-start' : ''}`}
                            onClick={() => isCurrentMonth && handleDateClick(dateStr)}
                            onMouseEnter={() => isCurrentMonth && setHoverDate(dateStr)}
                            onMouseLeave={() => setHoverDate('')}
                            disabled={!isCurrentMonth}
                          >
                            {day.getDate()}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(CalendarDatePicker);